import React, { JSX, useState } from "react";
import { useAenProviderFee } from "utilities/hooks/models/aen-resources/use-aen-provider-fee"; 
import { validatePageAccess } from "utilities/decorators/aspects/authorization/validate-page-access";
import { AccessControlKeys } from "utilities/enumerations/authorization/access-control-keys";
import { AenProviderFeeRecord } from "models/view-models/aen-resources/aen-provider-fee-record";
import { DataTable } from "components/tables/data-table/data-table";
import { ContextMenu } from "components/context-menu/context-menu/context-menu";
import { ContextMenuButton } from "components/context-menu/context-menu-button/context-menu-button";
import { Modal, ModalAction } from "components/modal/modal";
import { Pager } from "components/pager/pager";
import { AenProviderFeeModal } from "./admin-provider-fee-modal";
import { t } from "utilities/localization/t";
import { ButtonSize, ButtonStyle } from "components/buttons/button/button";
import { ToastManager } from "utilities/toast/toast-manager";
import { HeaderBanner } from "components/header-banner/header-banner";
import { Icons } from "components/icons/constants/icons";
import { ButtonIcon } from "components/buttons/button-icon/button-icon";
import { TabbedLayout, TabbedLayoutItem } from "components/tabs/tabbed-layout/tabbed-layout";
import { SkipNavContent } from "@chakra-ui/skip-nav";
import { TabProps } from "components/tabs/tabs/tab/tab";
import { DateUtils } from "utilities/date-utils";
import "./admin-provider-fee-list-page.scss";

interface AdminAenProviderFeeListPageProps {
    tabbedLayoutItems?: TabbedLayoutItem<TabProps>[]
}

const CSS_CLASS_NAME = "aen-provider-fee-list-page";
const ITEMS_PER_PAGE = 20;

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------
const AdminAenProviderFeeListPage: React.FC<AdminAenProviderFeeListPageProps> = validatePageAccess(
    AccessControlKeys.AdminAenResourcesListPage
)((props: AdminAenProviderFeeListPageProps): JSX.Element => {
    
    const currentYear = new Date().getFullYear();
    const [selectedAenProviderFee, setSelectedAenProviderFee] = useState<AenProviderFeeRecord>();
    const [aenProviderFeeToDelete, setAenProviderFeeToDelete] = useState<AenProviderFeeRecord | undefined>();
    const [editMode, setEditMode] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [showProviderFeeModal, setShowProviderFeeModal] = useState(false);
    const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);

    const { 
        aenProviderFee, 
        rowCount, 
        addAenProviderFee, 
        updateAenProviderFee, 
        deleteAenProviderFee,
    } = useAenProviderFee({
    });

    const maxYear = () => {
        const maxYear = Math.max(...aenProviderFee.map((r) => r.year));
        return maxYear;
    };

    const onPageClick = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    const deleteConfirmationActionArray: ModalAction[] = [
        {
            buttonText: t("cancel"),
            onClick: () => setShowDeleteConfirmationModal(false),
            style: ButtonStyle.Secondary,
        },
        {
            buttonText: t("yesRemove"),
            onClick: () => handleDeleteAenProviderFee(),
            style: ButtonStyle.Destructive,
        },
    ];

    const handleDeleteAenProviderFee = async () => {
        if (aenProviderFeeToDelete == null || aenProviderFeeToDelete.id == null) {
            ToastManager.error(t("thereWasAnIssueDeletingTheProviderFee"));
            return;
        }

        await deleteAenProviderFee(aenProviderFeeToDelete);
        setShowDeleteConfirmationModal(false);
    };

    const handleModalOpenEdit = (providerFee: AenProviderFeeRecord, idx: number) => {
        setEditMode(true);
        setSelectedAenProviderFee(providerFee);
        setShowProviderFeeModal(true);
    };

    const handleSaveAenProviderFee = async (aenResource: AenProviderFeeRecord) => {
        if (aenResource == null) {
            ToastManager.error(t("therewasanissuesavingthecontent"));
            return;
        }
        if (editMode) {
            await updateAenProviderFee(aenResource);
        } else {
            await addAenProviderFee(aenResource);
        }
        setShowProviderFeeModal(false);
    };
    
    const handleModalOpenNewProviderFee = () => {
        setEditMode(false);

        const newProviderFee = new AenProviderFeeRecord({
            year: maxYear() + 1,
            amount: undefined,
            deadline: undefined,
        });

        setSelectedAenProviderFee(newProviderFee);
        setShowProviderFeeModal(true);
    };

    const closeProviderFeeModal = () => {
        setSelectedAenProviderFee(undefined);
        setShowProviderFeeModal(false);
    };

    return (
        <div>
        <HeaderBanner title={t("aenProviderFees")}>
        <ButtonIcon
            text={t("addNewProviderFee")}
            onClick={() => handleModalOpenNewProviderFee()}
            buttonSize={ButtonSize.Medium}
            buttonStyle={ButtonStyle.TertiaryAlt}
            iconType={Icons.Plus}
        />
        </HeaderBanner>
        <SkipNavContent>
                <div className={CSS_CLASS_NAME}>
                    <TabbedLayout
                        cssClassName={`${CSS_CLASS_NAME}__tabs`}
                        tabs={props.tabbedLayoutItems!}
                    />
                    <div className={`${CSS_CLASS_NAME}__header`}>
                        <h2>
                            {t("aenProviderFees")}
                        </h2>
                    </div>

                    <DataTable cssClassName={CSS_CLASS_NAME}>
                        <thead>
                            <tr>
                                <th className="year">{t("year")}</th>
                                <th className="amount">{t("amount")}</th>
                                <th className="deadline">{t("deadline")}</th>
                                <th className="action">{t("action")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {aenProviderFee.map((providerFee, idx) => (
                                <tr key={providerFee.id}>
                                    <td className="year">{providerFee.year}</td>
                                    <td className="amount">${providerFee.amount?.toFixed(2)} USD</td>
                                    <td className="deadline">
                                        {providerFee.deadline?.toString() && DateUtils.formatDateCustom(providerFee.deadline.toString(), t("shortDateFormat"))}
                                    </td>
                                    <td className="action">
                                        <ContextMenu>
                                            <ContextMenuButton
                                                onClick={() =>
                                                    handleModalOpenEdit(providerFee, idx)
                                                }
                                                displayName={t("update")}
                                            />
                                            {(providerFee.year === maxYear() && providerFee.year > currentYear) && (
                                                <ContextMenuButton
                                                    onClick={() => { setAenProviderFeeToDelete(providerFee); setShowDeleteConfirmationModal(true); }}
                                                    displayName={t("delete")}
                                                />
                                            )}
                                        </ContextMenu>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </DataTable>
                    <Pager
                        currentPage={currentPage}
                        onPageClick={onPageClick}
                        totalPages={rowCount / ITEMS_PER_PAGE}
                        itemsPerPage={ITEMS_PER_PAGE}
                        totalItems={rowCount}></Pager>
                </div>
            </SkipNavContent>                  
                <Modal
                    isOpen={showDeleteConfirmationModal}
                    onModalClose={() => setShowDeleteConfirmationModal(false)}
                    actions={deleteConfirmationActionArray}
                    modalStyle={"-inverted"}>
                    {t("areYouSureYouWantToRemoveProviderFee")}
                </Modal>

                <AenProviderFeeModal
                    open={showProviderFeeModal}
                    setOpen={closeProviderFeeModal}
                    aenProviderFee={selectedAenProviderFee}
                    editing={editMode}
                    saveAenProviderFee={handleSaveAenProviderFee}
                />
        </div>
    );
});

export default AdminAenProviderFeeListPage;